/**
 * Override List Component
 *
 * Displays list of SLA overrides grouped by client with active indicators.
 *
 * @module components/SLA/OverrideList
 */

import React from 'react';
import { ClientSLAOverride, SLAAttributeName } from '@shared/types/sla';
import { formatEastern } from '@/utils/dateFormatter';

interface OverrideListProps {
  /** List of overrides to display */
  overrides: ClientSLAOverride[];
  /** Callback when edit button is clicked */
  onEdit: (override: ClientSLAOverride) => void;
  /** Callback when delete button is clicked */
  onDelete: (overrideId: string) => void;
}

const ATTRIBUTE_LABELS: Record<SLAAttributeName, string> = {
  uptime_percent: 'Uptime %',
  rto_hours: 'RTO (hours)',
  rpo_hours: 'RPO (hours)',
  penalty_per_hour_usd: 'Penalty ($/hour)',
};

/**
 * Override List - Display and manage SLA overrides
 */
const OverrideList: React.FC<OverrideListProps> = ({ overrides, onEdit, onDelete }) => {
  const isActive = (override: ClientSLAOverride): boolean => {
    if (!override.expiration_date) return true;
    return new Date(override.expiration_date) > new Date();
  };

  const formatDate = (date: string) => {
    return formatEastern(date);
  };

  // Group overrides by client
  const overridesByClient = overrides.reduce(
    (acc, override) => {
      const clientId = override.client_id;
      if (!acc[clientId]) {
        acc[clientId] = [];
      }
      acc[clientId].push(override);
      return acc;
    },
    {} as Record<string, ClientSLAOverride[]>
  );

  if (overrides.length === 0) {
    return (
      <div className='text-center py-12 border border-dashed border-[var(--color-border)] rounded-lg'>
        <svg
          className='mx-auto h-12 w-12 text-[var(--color-text-secondary)]'
          fill='none'
          viewBox='0 0 24 24'
          stroke='currentColor'
        >
          <path
            strokeLinecap='round'
            strokeLinejoin='round'
            strokeWidth={2}
            d='M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4'
          />
        </svg>
        <h3 className='mt-2 text-sm font-medium text-[var(--color-text-primary)]'>No overrides</h3>
        <p className='mt-1 text-sm text-[var(--color-text-secondary)]'>
          Get started by creating an override for a client's SLA attributes.
        </p>
      </div>
    );
  }

  return (
    <div className='space-y-6'>
      {Object.entries(overridesByClient).map(([clientId, clientOverrides]) => (
        <div key={clientId} className='bg-[var(--color-bg-card)] rounded-lg shadow overflow-hidden'>
          {/* Client Header */}
          <div className='bg-[var(--color-bg-subtle)] px-4 py-3 border-b border-[var(--color-border)]'>
            <h4 className='text-sm font-medium text-[var(--color-text-primary)]'>
              Client: {clientId}
              <span className='ml-2 text-xs text-[var(--color-text-secondary)]'>
                ({clientOverrides.length} override{clientOverrides.length !== 1 ? 's' : ''})
              </span>
            </h4>
          </div>

          {/* Overrides Table */}
          <div className='overflow-hidden'>
            <table className='min-w-full divide-y divide-[var(--color-border)]'>
              <thead className='bg-[var(--color-bg-subtle)]'>
                <tr>
                  <th className='px-4 py-3 text-left text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wider'>
                    Attribute
                  </th>
                  <th className='px-4 py-3 text-left text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wider'>
                    Value
                  </th>
                  <th className='px-4 py-3 text-left text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wider'>
                    Status
                  </th>
                  <th className='px-4 py-3 text-left text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wider'>
                    Expiration
                  </th>
                  <th className='relative px-4 py-3 text-right text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wider'>
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className='bg-[var(--color-bg-card)] divide-y divide-[var(--color-border)]'>
                {clientOverrides.map((override) => {
                  const active = isActive(override);
                  return (
                    <tr key={override.id} className={active ? '' : 'bg-[var(--color-bg-subtle)]'}>
                      <td className='px-4 py-3 whitespace-nowrap text-sm font-medium text-[var(--color-text-primary)]'>
                        {ATTRIBUTE_LABELS[override.attribute_name]}
                      </td>
                      <td className='px-4 py-3 whitespace-nowrap text-sm text-[var(--color-text-secondary)]'>
                        {override.override_value}
                        {override.attribute_name === 'uptime_percent' && '%'}
                        {(override.attribute_name === 'rto_hours' ||
                          override.attribute_name === 'rpo_hours') &&
                          'h'}
                        {override.attribute_name === 'penalty_per_hour_usd' && ' $/h'}
                      </td>
                      <td className='px-4 py-3 whitespace-nowrap text-sm'>
                        {active ? (
                          <span className='inline-flex items-center rounded-full bg-green-100 dark:bg-green-900/30 px-2.5 py-0.5 text-xs font-medium text-green-800 dark:text-green-300'>
                            <svg className='mr-1 h-3 w-3' fill='currentColor' viewBox='0 0 8 8'>
                              <circle cx={4} cy={4} r={3} />
                            </svg>
                            Active
                          </span>
                        ) : (
                          <span className='inline-flex items-center rounded-full bg-gray-100 dark:bg-gray-700 px-2.5 py-0.5 text-xs font-medium text-gray-800 dark:text-gray-300'>
                            Expired
                          </span>
                        )}
                      </td>
                      <td className='px-4 py-3 whitespace-nowrap text-sm text-[var(--color-text-secondary)]'>
                        {override.expiration_date ? formatDate(override.expiration_date) : 'Never'}
                      </td>
                      <td className='px-4 py-3 whitespace-nowrap text-right text-sm font-medium space-x-2'>
                        <button
                          type='button'
                          onClick={() => onEdit(override)}
                          className='text-[var(--color-accent)] hover:brightness-110'
                        >
                          Edit
                        </button>
                        <button
                          type='button'
                          onClick={() => onDelete(override.id)}
                          className='text-red-600 dark:text-red-400 hover:text-red-900 dark:hover:text-red-300'
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      ))}
    </div>
  );
};

export default OverrideList;
export { OverrideList };
